/**
 * 音乐列表缓存管理器
 * 用于避免在应用初始化时多次请求 /api/music/list
 */

import { getMusicList as originalGetMusicList } from '@/api/music';

class MusicListCache {
  constructor() {
    // 缓存的音乐列表数据
    this.cachedData = null;
    
    // 缓存时间戳
    this.cacheTimestamp = null;
    
    // 缓存有效期（毫秒），默认5分钟
    this.cacheExpiry = 5 * 60 * 1000;
    
    // 正在进行的请求 Promise（用于请求去重）
    this.pendingRequest = null;
    
    // 是否启用缓存（默认启用）
    this.enabled = true;
  }

  /**
   * 检查缓存是否有效
   */
  isCacheValid() {
    if (!this.enabled || !this.cachedData || !this.cacheTimestamp) {
      return false;
    }
    
    const now = Date.now();
    const isValid = (now - this.cacheTimestamp) < this.cacheExpiry;
    
    if (!isValid) {
      console.log('🗑️ [音乐列表缓存] 缓存已过期');
    }
    
    return isValid;
  }

  /**
   * 获取音乐列表（带缓存和去重）
   */
  async getMusicList() {
    // 如果缓存有效，直接返回缓存数据
    if (this.isCacheValid()) {
      console.log('✅ [音乐列表缓存] 使用缓存数据，避免重复请求');
      return this.cachedData;
    }

    // 如果有正在进行的请求，等待该请求完成（请求去重）
    if (this.pendingRequest) {
      console.log('⏳ [音乐列表缓存] 检测到正在进行的请求，等待完成（请求去重）');
      return this.pendingRequest;
    }

    // 发起新请求
    console.log('🌐 [音乐列表缓存] 发起新的音乐列表请求');
    this.pendingRequest = originalGetMusicList()
      .then(response => {
        // 缓存响应数据
        this.cachedData = response;
        this.cacheTimestamp = Date.now();
        console.log('✅ [音乐列表缓存] 请求成功，已缓存数据');
        
        // 清除 pending 状态
        this.pendingRequest = null;
        
        return response;
      })
      .catch(error => {
        console.error('❌ [音乐列表缓存] 请求失败:', error);
        
        // 清除 pending 状态
        this.pendingRequest = null;
        
        throw error;
      });

    return this.pendingRequest;
  }

  /**
   * 清除缓存（在上传、删除音乐等操作后调用）
   */
  clearCache() {
    console.log('🗑️ [音乐列表缓存] 手动清除缓存');
    this.cachedData = null;
    this.cacheTimestamp = null;
  }

  /**
   * 强制刷新（清除缓存并重新请求）
   */
  async forceRefresh() {
    console.log('🔄 [音乐列表缓存] 强制刷新');
    this.clearCache();
    return this.getMusicList();
  }

  /**
   * 设置缓存有效期
   */
  setCacheExpiry(milliseconds) {
    this.cacheExpiry = milliseconds;
    console.log(`⚙️ [音乐列表缓存] 缓存有效期已设置为 ${milliseconds}ms`);
  }

  /**
   * 启用/禁用缓存
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    console.log(`⚙️ [音乐列表缓存] 缓存已${enabled ? '启用' : '禁用'}`);
    
    if (!enabled) {
      this.clearCache();
    }
  }

  /**
   * 获取缓存状态信息
   */
  getStatus() {
    return {
      enabled: this.enabled,
      hasCachedData: !!this.cachedData,
      cacheTimestamp: this.cacheTimestamp,
      cacheAge: this.cacheTimestamp ? Date.now() - this.cacheTimestamp : null,
      isValid: this.isCacheValid(),
      hasPendingRequest: !!this.pendingRequest
    };
  }
}

// 创建单例实例
const musicListCache = new MusicListCache();

// 导出缓存实例和包装后的 getMusicList 函数
export default musicListCache;

/**
 * 包装后的 getMusicList 函数（带缓存和去重）
 * 用于替换原有的 getMusicList 导入
 */
export const getMusicList = () => musicListCache.getMusicList();

/**
 * 清除音乐列表缓存
 * 在上传、删除、修改音乐后调用
 */
export const clearMusicListCache = () => musicListCache.clearCache();

/**
 * 强制刷新音乐列表
 */
export const forceRefreshMusicList = () => musicListCache.forceRefresh();

// 在开发环境下暴露到全局，方便调试
if (process.env.NODE_ENV === 'development') {
  window.musicListCache = musicListCache;
  console.log('🎵 音乐列表缓存管理器已暴露到全局 (window.musicListCache)');
}
